import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import _ from "lodash";
import { toast } from "react-toastify";

import { getAvatarUrl, getImageUrl } from "src/Common/common";
import TechnicianService from "src/Services/API/technician.service";
import UserService from "src/Services/API/user.service";
import { can, handleError } from "src/Common/function";
import { addTimeValues } from "src/Common/hours";
import DayEntriesEditor, { entryRegular, entryTravel } from "src/Common/DayEntriesEditor";

const EMPTY_TIME = "00:00";

/**
 * One technician's hours for one day of a work order.
 *
 * Hours are captured as start/stop clock times rather than typed durations, the
 * same way the technician app captures them. Ordered timestamps are what let a
 * day be split at an overtime threshold, and they leave an audit trail a bare
 * duration cannot. A day can hold several stretches — worked in the morning,
 * back after a gap — so the row summarises the day and expands to edit it.
 *
 * Overtime is not entered here. It is derived from the stretches, as it is
 * everywhere else; a figure already stored on a day is carried through a save
 * untouched so nothing is wiped, but there is no box to type a new one into.
 */
const DayTechItem = ({
  tech,
  currentDate,
  startDateString,
  timesheetUpdated,
  activeEditingTechId,
  setActiveEditingTechId,
  woStatus,
}) => {
  const [techData, setTechData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditing = activeEditingTechId === tech?.id;
  const isFutureStartDate = moment(startDateString).isAfter(moment(), "day");

  const dayDiff = useMemo(
    () => moment(currentDate).diff(moment(startDateString), "days"),
    [currentDate, startDateString]
  );

  // Every stretch stored against this day, in the order they were recorded.
  const storedDayEntries = (source) =>
    (source?.timesheet || []).filter((entry) => Number(entry.dayDiff) === dayDiff);

  useEffect(() => {
    const technician = _.cloneDeep(tech);
    const todays = storedDayEntries(tech);

    // The row shows one day, and a day can hold several stretches, so the
    // figures are summed rather than read off whichever entry came first.
    technician.regularTime = addTimeValues(todays.map(entryRegular));
    technician.travelTime = addTimeValues(todays.map((entry) => entryTravel(entry)));
    technician.overTime = addTimeValues(todays.map((entry) => entry.overTime));

    setTechData(technician);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tech, currentDate, startDateString]);

  const startEditing = () => setActiveEditingTechId(tech.id);
  const stopEditing = () => setActiveEditingTechId(null);

  // The check mark in the row is a second way out of edit mode; the editor's
  // own Save button is the way to write. Pressing it here just closes.
  const onEditButtonClick = () => {
    if (!isEditing) {
      if (activeEditingTechId && activeEditingTechId !== tech.id) return;
      startEditing();
      return;
    }
    stopEditing();
  };

  const saveTimesheet = (dayEntries) => {
    if (isSubmitting) return;

    // The day is the unit being saved. Replacing it wholesale rather than
    // editing entries in place is what lets a stretch be removed at all —
    // matching entries up individually would need an identity they have never
    // carried. Every other day is passed through untouched.
    const otherDays = (techData.timesheet || []).filter(
      (entry) => Number(entry.dayDiff) !== dayDiff
    );
    const timesheet = [...otherDays, ...dayEntries.map((entry) => ({ dayDiff, ...entry }))];

    setIsSubmitting(true);
    // The office route with wo:logTimesheet; otherwise their own hours.
    const save = officeCanEdit ? TechnicianService.updateTimesheet : TechnicianService.updateOwnTimesheet;
    save({
      id: techData.id,
      data: { timesheet: JSON.stringify(timesheet) },
    })
      .then(() => {
        toast("Timesheet updated successfully");
        stopEditing();
        timesheetUpdated();
      })
      .catch((error) => handleError(error))
      .finally(() => setIsSubmitting(false));
  };

  const readOnlyValue = (value) => (value && value !== EMPTY_TIME ? value : "__:__");

  const officeCanEdit =
    can("wo:logTimesheet") && (woStatus < 100 || (woStatus === 100 && can("wo:logBilledTimesheet")));
  // A technician's own row (an accepted assignment), as in the tech app: not
  // once the work order is complete, and not a day that hasn't happened yet.
  const isOwnRow = techData?.user?.id === UserService.getCurrentUser()?.id;
  const ownCanEdit =
    isOwnRow &&
    Number(techData?.status) === 1 &&
    woStatus < 5 &&
    !moment(currentDate).isAfter(moment(), "day");
  const canEdit = !isFutureStartDate && (officeCanEdit || ownCanEdit);

  return (
    techData && (
      <div className="w-full border-t-[1px] border-borderColor">
        <div className="w-full h-[40px] flex items-center px-3 relative">
          <img src={getAvatarUrl(techData.user.avatar)} className="w-[24px] rounded-[12px]" alt="" />
          <p className="font-OpenSansRegular text-[14px] font-[400] text-titleColor ml-2 flex-1 min-w-0 truncate mr-[calc(5%+325px)]">
            {techData.user.name}
          </p>

          <div className="absolute right-[5%] top-[3px] h-[34px] flex items-center gap-3">
            <p className="font-OpenSansRegular text-[14px] font-[400] text-titleColor w-[96px] text-center">
              {readOnlyValue(techData.regularTime)}
            </p>
            <p className="font-OpenSansRegular text-[14px] font-[400] text-titleColor w-[96px] text-center">
              {readOnlyValue(techData.travelTime)}
            </p>
            <p className="font-OpenSansRegular text-[14px] font-[400] text-titleColor w-[96px] text-center">
              {readOnlyValue(techData.overTime)}
            </p>
          </div>

          {/* Timesheet save always hits /update_timesheet_staff (wo:logTimesheet-gated),
            so logging on a billed WO needs wo:logTimesheet AND the extra
            wo:logBilledTimesheet — the latter unlocks editing once billed. */}
          {canEdit && (
            <div className="absolute right-[1%] top-[3px]">
              <EditButton
                clicked={onEditButtonClick}
                isEditing={isEditing}
                isDisabled={!isEditing && activeEditingTechId !== null && activeEditingTechId !== tech.id}
              />
            </div>
          )}
        </div>

        {isEditing && (
          <DayEntriesEditor
            className="border-t-[1px] border-borderColor"
            resetKey={`${tech.id}|${dayDiff}`}
            initialEntries={storedDayEntries(techData)}
            saving={isSubmitting}
            onSave={saveTimesheet}
            onCancel={stopEditing}
          />
        )}
      </div>
    )
  );
};

export default DayTechItem;

const EditButton = ({ clicked, isEditing = false, isDisabled = false }) => {
  // When editing, swallow mousedown so a focused input doesn't blur before the
  // click handler runs.
  const handleMouseDown = (event) => {
    if (isEditing) {
      event.preventDefault();
    }
  };

  return isEditing ? (
    <div
      onMouseDown={handleMouseDown}
      onClick={clicked}
      className="w-[18px] absolute right-[5%] top-[12px] cursor-pointer"
    >
      <img src={getImageUrl("/images/common/check.png")} className="w-[18px]" alt="" />
    </div>
  ) : (
    <div
      onClick={isDisabled ? undefined : clicked}
      className={`w-[18px] absolute right-[5%] top-[12px] ${isDisabled ? "cursor-not-allowed opacity-40" : "cursor-pointer"}`}
    >
      <img src={getImageUrl("/images/common/blueedit.png")} className="w-[18px]" alt="" />
    </div>
  );
};
